import { Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import { IUserGroup, UserGroupService } from '../../../services/UserGroup/UserGroupService'
import { UserGroupCreateModal } from './UserGroupCreateModal'
import { UserGroupEditModal } from './UserGroupEditModal'

export default function UsersGroupsPage() {
  const userGroupService = new UserGroupService()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userGroups, setUserGroups] = useState<IUserGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isUserGroupCreateModalOpen, setIsUserGroupCreateModalOpen] = useState(false)
  const { isOpen: isOpenDeleteDialog, onOpen: onOpenDeleteDialog, onClose: onCloseDeleteDialog } = useDisclosure()
  const [modalUser, setModalUser] = useState<IUserGroup | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useAppToast()
  const [isUserGroupEditModalOpen, setIsUserGroupEditModalOpen] = useState(false)
  const hasRole = useHasRole()
  const permissions = {
    addGroup: hasRole('add_group'),
    editGroup: hasRole('edit_group'),
    deleteGroup: hasRole('delete_group')
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      loadUserGroups(page)
    }, 500)
    return () => clearTimeout(handler)
  }, [page, pageSize])

  const loadUserGroups = async (page: number) => {
    setLoading(true)
    setLoadError(false)
    try {
      const { total_selected, per_page, data } = await userGroupService.list({ page, pageSize })

      setUserGroups(data)

      setTotalPages(Math.ceil(total_selected / per_page))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os grupos', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseUserGroupCreateModal = () => {
    setIsUserGroupCreateModalOpen(false)
    loadUserGroups(page)
  }

  const handleCloseUserGroupEditModal = () => {
    setIsUserGroupEditModalOpen(false)
    loadUserGroups(page)
  }

  const handleDelete = async () => {
    if (!modalUser) {
      return
    }

    setIsDeleting(true)
    try {
      await userGroupService.destroy(modalUser.id)
      toast.success('Grupo de usuários excluído')
      onCloseDeleteDialog()
      loadUserGroups(page)
    } catch (error) {
      toast.error('Erro ao excluir grupo de usuários', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <PageTitle title="Grupos de Usuários" description="Grupos que definem as permissões de acesso" />
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome do Grupo</Th>
              <Th>Criado por</Th>
              <Th>Data de Criação</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="grupos" onRetry={() => loadUserGroups(page)} />
            ) : userGroups.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="grupo" />
            ) : (
              userGroups.map((userGroup) => (
                <Tr key={userGroup.id}>
                  <Td>{userGroup.name}</Td>
                  <Td>{userGroup.creator.name}</Td>
                  <Td>{formatDateTime(userGroup.created_at)}</Td>
                  <Td>
                    {permissions.addGroup && (
                      <ActionButton
                        action="duplicate"
                        onClick={() => {
                          setModalUser(userGroup)
                          setIsUserGroupCreateModalOpen(true)
                        }}
                      />
                    )}
                    {permissions.editGroup && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setIsUserGroupEditModalOpen(true)
                          setModalUser(userGroup)
                        }}
                      />
                    )}

                    {permissions.deleteGroup && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setModalUser(userGroup)
                          onOpenDeleteDialog()
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadUserGroups(page)
        }}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <UserGroupCreateModal
        isOpen={isUserGroupCreateModalOpen}
        onClose={handleCloseUserGroupCreateModal}
        userGroup={modalUser}
      />
      {modalUser && (
        <UserGroupEditModal
          isOpen={isUserGroupEditModalOpen}
          onClose={handleCloseUserGroupEditModal}
          userGroup={modalUser}
        />
      )}
      {modalUser && (
        <ConfirmDialog
          isOpen={isOpenDeleteDialog}
          onClose={onCloseDeleteDialog}
          onConfirm={handleDelete}
          action="delete"
          entity="grupo de usuários"
          name={modalUser?.name}
          isLoading={isDeleting}
        />
      )}

      {permissions.addGroup && (
        <ActionFab
          label="Adicionar grupo de usuários"
          onClick={() => {
            setModalUser(null)
            setIsUserGroupCreateModalOpen(true)
          }}
        />
      )}
    </>
  )
}
